"use client"

import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import config from "../config"
import { Helmet } from 'react-helmet-async'

const EditEvent = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const { event_slug } = useParams()
  const navigate = useNavigate()
  const { showSuccessModal, showFailureModal } = useSuccessFailure()

  // State management
  const [event, setEvent] = useState(null)
  const [categories, setCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    location_url: "",
    date: "",
    time: "",
    category: "",
    keywords: "",
    thumbnail_photo: null,
    is_paid_event: false,
    ticket_price: "",
    payment_instructions: "",
    payment_deadline: ""
  })

  // Constants for validation
  const MAX_KEYWORDS = 4
  const MAX_KEYWORD_LENGTH = 50

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/events/categories/`)
        setCategories(response.data.categories)
      } catch (error) {
        // console.error("Error fetching categories:", error)
        console.error("Error fetching categories:")
      } finally {
        setLoadingCategories(false)
      }
    }

    fetchCategories()
  }, [])

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/events/${event_slug}/`)
        const eventData = response.data

        // Format date and time for the form
        const eventDate = new Date(eventData.date)
        const formattedDate = eventDate.toISOString().split('T')[0]
        const formattedTime = eventData.time || ''

        setEvent(eventData)
        setFormData({
          title: eventData.title || '',
          description: eventData.description || '',
          location: eventData.location || '',
          location_url: eventData.location_url || '',
          date: formattedDate,
          time: formattedTime,
          category: eventData.category || '',
          keywords: eventData.keywords || '',
          thumbnail_photo: null, // Keep as null to avoid serialization issues
          is_paid_event: eventData.is_paid_event || false,
          ticket_price: eventData.ticket_price || '',
          payment_instructions: eventData.payment_instructions || '',
          payment_deadline: eventData.payment_deadline ? eventData.payment_deadline.split('T')[0] : ''
        })
      } catch (error) {
        // console.error('Error fetching event:', error)
        console.error('Error fetching event:')
        showFailureModal('Failed to load event. Please try again.')
        navigate('/my-events')
      }
    }

    if (event_slug) {
      fetchEvent()
    }
  }, [event_slug, navigate, showFailureModal])

  // Validation functions
  const validateKeywords = (keywords) => {
    const errors = []
    const trimmedKeywords = keywords.map((k) => k.trim())
    const filteredKeywords = trimmedKeywords.filter((k) => k.length > 0)

    if (filteredKeywords.length > MAX_KEYWORDS) {
      errors.push(`Maximum ${MAX_KEYWORDS} keywords allowed`)
    }

    const longKeywords = filteredKeywords.filter((k) => k.length > MAX_KEYWORD_LENGTH)
    if (longKeywords.length > 0) {
      errors.push(`Each keyword cannot be longer than ${MAX_KEYWORD_LENGTH} characters`)
    }

    return errors
  }

  const validateFormData = () => {
    const keywords = formData.keywords ? formData.keywords.split(",").map((k) => k.trim()) : []
    const keywordErrors = validateKeywords(keywords)

    if (keywordErrors.length > 0) {
      const errorDiv = document.getElementById("keywordError")
      if (errorDiv) {
        errorDiv.textContent = keywordErrors.join("\n")
      }
      return false
    }
    return true
  }

  // Event handlers
  const handleChange = (e) => {
    const { name, value, type, files } = e.target

    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }))
      return
    }

    if (name === "keywords") {
      setFormData((prev) => ({ ...prev, keywords: value }))

      const keywords = value.split(",").map((k) => k.trim())
      const keywordErrors = validateKeywords(keywords)

      const errorDiv = document.getElementById("keywordError")
      if (errorDiv) {
        errorDiv.textContent = keywordErrors.length > 0 ? keywordErrors.join("\n") : ""
      }
      return
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      const currentValue = e.target.value
      const keywords = currentValue.split(",")

      const errors = []
      if (keywords.length > 4) {
        errors.push("Maximum 4 keywords allowed")
      }

      const longKeywords = keywords.filter((k) => k.trim().length > 0 && k.trim().length > 50)
      if (longKeywords.length > 0) {
        errors.push("Each keyword cannot be longer than 50 characters")
      }

      if (errors.length > 0) {
        const errorDiv = document.getElementById("keywordError")
        if (errorDiv) {
          errorDiv.textContent = errors.join("\n")
        }
        return
      }

      if (currentValue && !currentValue.endsWith(",")) {
        e.target.value = currentValue + ","
        setFormData({ ...formData, keywords: currentValue + "," })
      }
    }
  }

  const removeKeyword = (keyword) => {
    const currentKeywords = formData.keywords ? formData.keywords.split(",") : []
    const newKeywords = currentKeywords.filter((k) => k.trim() !== keyword.trim())
    setFormData((prev) => ({ ...prev, keywords: newKeywords.join(",") }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Validate form
    if (!formData.thumbnail_photo && !event?.thumbnail_photo) {
      showFailureModal('Please upload a thumbnail photo')
      setIsSubmitting(false)
      return
    }

    // Validate payment fields if it's a paid event
    if (formData.is_paid_event) {
      if (!formData.ticket_price || parseFloat(formData.ticket_price) <= 0) {
        showFailureModal('Please enter a valid ticket price')
        setIsSubmitting(false)
        return
      }
      if (!formData.payment_instructions) {
        showFailureModal('Please provide payment instructions for attendees')
        setIsSubmitting(false)
        return
      }
    }

    // Prepare form data
    const data = new FormData()
    Object.keys(formData).forEach((key) => {
      // Skip empty strings for optional fields
      if (formData[key] === '') return

      // Convert boolean to string for FormData
      if (key === 'is_paid_event') {
        data.append(key, formData[key] ? 'true' : 'false')
      }
      // Handle file upload
      else if (key === 'thumbnail_photo' && formData[key]) {
        data.append(key, formData[key])
      }
      // Handle other fields
      else if (formData[key] !== null) {
        data.append(key, formData[key])
      }
    })

    try {
      const token = localStorage.getItem("access_token")
      const response = await axios.put(`${config.API_BASE_URL}/events/${event_slug}/update/`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })

      if (response.status === 200) {
        showSuccessModal("Event details updated successfully")
        navigate(`/events/${event_slug}`)
      } else {
        showFailureModal("Event details were not updated successfully")
      }
    } catch (err) {
      showFailureModal("Failed to update event.")
      // console.error("Update failed:", err)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Delete handlers
  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/events/${event_slug}/soft_delete/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )
      showSuccessModal(response.data.message)
      navigate("/my-events")
    } catch (error) {
      showFailureModal(error.response?.data?.error || "Failed to delete event")
    }
    setShowDeleteConfirm(false)
  }

  // Restore handlers
  const handleRestore = () => {
    setShowRestoreConfirm(true)
  }

  const handleConfirmRestore = async () => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/events/${event_slug}/restore/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )
      showSuccessModal(response.data.message)
      navigate("/my-events")
    } catch (error) {
      showFailureModal(error.response?.data?.error || "Failed to restore event")
    }
    setShowRestoreConfirm(false)
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Edit Event - ${eventTitle}`}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Edit Event</h1>
              <div className="flex gap-2">
                {event.is_deleted ? (
                  <button
                    onClick={handleRestore}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
                  >
                    Restore Event
                  </button>
                ) : (
                  <button
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
                  >
                    Delete Event
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows={8}
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>

                {/* Paid Event Toggle */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_paid_event"
                    name="is_paid_event"
                    checked={formData.is_paid_event}
                    onChange={(e) => setFormData({ ...formData, is_paid_event: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_paid_event" className="text-sm font-medium text-gray-700">
                    This is a paid event
                  </label>
                </div>

                {/* Payment Fields - Only show if paid event */}
                {formData.is_paid_event && (
                  <>
                    <div className="md:col-span-2 border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ticket Price (KES)
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">KSh</span>
                            </div>
                            <input
                              type="number"
                              name="ticket_price"
                              min="0"
                              step="0.01"
                              value={formData.ticket_price}
                              onChange={handleChange}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md p-2 border"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Deadline
                          </label>
                          <input
                            type="date"
                            name="payment_deadline"
                            value={formData.payment_deadline}
                            onChange={handleChange}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Instructions
                            <span className="text-red-500">*</span>
                            <p className="text-xs text-gray-500 mt-1">
                              Provide clear instructions for how attendees should make payments
                            </p>
                          </label>
                          <textarea
                            name="payment_instructions"
                            rows={4}
                            value={formData.payment_instructions}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            placeholder="Example: Send payment to M-Pesa Paybill 123456, Account: Your Name"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location URL</label>
                  <input
                    type="url"
                    name="location_url"
                    value={formData.location_url}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="keywords"
                      value={formData.keywords}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Enter keywords separated by commas"
                      className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.querySelector('input[name="keywords"]')
                        if (input) {
                          const value = input.value.trim()
                          if (value && !value.endsWith(",")) {
                            input.value = value + ","
                            setFormData({ ...formData, keywords: value + "," })
                          }
                        }
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      title="Add comma"
                    >
                      ,
                    </button>
                  </div>
                  <div className="text-sm text-red-500 h-4 mt-1" id="keywordError"></div>
                  <p className="text-sm text-gray-500 mt-1">Maximum 4 keywords, each up to 50 characters</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail Photo</label>
                  <input
                    type="file"
                    name="thumbnail_photo"
                    onChange={handleChange}
                    accept="image/*"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />

                  {event.thumbnail_photo && !formData.thumbnail_photo && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-2">Current thumbnail:</p>
                      <img
                        src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
                        alt="Current thumbnail"
                        className="w-32 h-32 object-cover rounded-lg shadow cursor-pointer"
                        onClick={() => setIsImageModalOpen(true)}
                      />
                    </div>
                  )}

                  {formData.thumbnail_photo && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-2">New thumbnail:</p>
                      <img
                        src={URL.createObjectURL(formData.thumbnail_photo) || "/placeholder.svg"}
                        alt="New thumbnail"
                        className="w-32 h-32 object-cover rounded-lg shadow cursor-pointer"
                        onClick={() => setIsImageModalOpen(true)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Updating..." : "Update Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this event? This action can be undone later.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Restore</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to restore this event?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowRestoreConfirm(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestore}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="max-w-4xl max-h-full p-4">
            <img
              src={
                formData.thumbnail_photo
                  ? URL.createObjectURL(formData.thumbnail_photo)
                  : `${config.MEDIA_BASE_URL}${event.thumbnail_photo}`
              }
              alt="Event thumbnail"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default EditEvent
